import {
  type Logo,
  type Social,
  windowService,
  type DefaultDomainConfig,
  DOMAINS,
} from '~/domains/core';
import has from 'lodash-es/has';
import { useDateFormatter } from '~/infrastructure';
import { computed, type ComputedRef } from 'vue';
import { getCurrentConfigByDomain } from '~/domains/core';

interface ReturnInterface {
  getLogoData: () => Logo;
  getSocialData: () => Array<{ name: string; link: string; icon: string }>;
  footerCopyright: ComputedRef<string>;
  loginFormTitle: ComputedRef<string>;
  updateFavicon: () => void;
  defaultConfig: DefaultDomainConfig;
  currentDomain: keyof typeof DOMAINS;
  isAsbc: ComputedRef<boolean>;
}

export default (): ReturnInterface => {
  const hostname = windowService.getLocation().hostname;
  const { currentYear } = useDateFormatter();
  const { currentConfig, defaultConfig, currentDomain } =
    getCurrentConfigByDomain(hostname);

  const getLogoData = (): Logo => {
    return currentConfig.logo ?? defaultConfig.logo;
  };

  const getSocialData = (): Social[] => {
    if (
      currentConfig.socials !== undefined &&
      has(currentConfig.socials, hostname)
    ) {
      return currentConfig.socials[hostname] as Social[];
    }

    return defaultConfig.socials as Social[];
  };

  const updateFavicon = (): void => {
    const faviconPath = currentConfig.favicon ?? defaultConfig.favicon;

    let link: HTMLLinkElement | null =
      document.querySelector("link[rel~='icon']");
    if (link !== null) {
      link.href = faviconPath;
    } else {
      link = document.createElement('link');
      link.rel = 'icon';
      link.href = faviconPath;
      document.head.appendChild(link);
    }
  };

  const footerCopyright: ComputedRef<string> = computed(
    () =>
      currentConfig.footerCopyright?.(currentYear.value) ??
      defaultConfig.footerCopyright(currentYear.value),
  );

  const loginFormTitle: ComputedRef<string> = computed(
    () => currentConfig.loginFormTitle ?? defaultConfig.loginFormTitle,
  );

  const isAsbc: ComputedRef<boolean> = computed(
    () => currentDomain === DOMAINS.ASBC,
  );

  return {
    getLogoData,
    getSocialData,
    updateFavicon,
    footerCopyright,
    loginFormTitle,
    defaultConfig,
    currentDomain,
    isAsbc,
  };
};
